import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import JsonLd from "@/components/base/JsonLd";
import { categories } from "@/mocks/categories";
import { comics } from "@/mocks/comics";

const site = "https://ymdbo14.cn";
const PAGE_SIZE = 8;

function parseViews(v: string) {
  const m = v.match(/^([\d.]+)万?$/);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  return v.includes("万") ? n * 10000 : n;
}

type SortKey = "update" | "views" | "rating";

export default function CategoryPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"all" | "连载中" | "已完结">("all");

  const activeId = id || "all";
  const activeCategory = categories.find((c) => c.id === activeId);
  const sortParam = searchParams.get("sort");
  const sortKey: SortKey = sortParam === "views" || sortParam === "rating" ? sortParam : "update";

  const filtered = useMemo(() => {
    let list = activeId === "all" ? [...comics] : comics.filter((c) => c.categoryId === activeId);
    if (status !== "all") list = list.filter((c) => c.status === status);
    list.sort((a, b) => {
      if (sortKey === "views") return parseViews(b.views) - parseViews(a.views);
      if (sortKey === "rating") return b.rating - a.rating;
      return b.updateTime.localeCompare(a.updateTime);
    });
    return list;
  }, [activeId, status, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const title = activeCategory ? `${activeCategory.name}漫画 - ${activeCategory.name}漫画大全` : "漫画大全 - 全部漫画";
  const desc = activeCategory
    ? `${activeCategory.name}漫画分类速查，收录${activeCategory.name}题材漫画大全，按漫画网漫画分类快速定位。`
    : "漫画网漫画大全收录，覆盖热血、恋爱、悬疑、奇幻、科幻、搞笑六大漫画网漫画分类速查。";

  const changeSort = (key: SortKey) => {
    const next = key === "update" ? {} : { sort: key };
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: title,
          url: `${site}/category/${activeId}`,
          inLanguage: "zh-CN",
          description: desc,
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${site}/` },
            { "@type": "ListItem", position: 2, name: "漫画分类", item: `${site}/category/all` },
            { "@type": "ListItem", position: 3, name: title },
          ],
        }}
      />

      {/* Page header */}
      <section className="bg-secondary-100/70 pb-8 pt-24 md:pt-28">
        <div className="container-x">
          <Breadcrumb
            items={[{ label: "漫画分类", to: "/category/all" }, { label: activeCategory ? `${activeCategory.name}漫画` : "全部漫画" }]}
          />
          <div className="mt-5">
            <h1 className="font-display text-2xl font-black text-foreground-900 md:text-3xl">{title}</h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">{desc}</p>
            <p className="mt-2 flex items-center gap-2 text-xs text-foreground-500 md:text-sm">
              <i className="ri-file-list-3-line text-primary-500"></i>
              共收录 {filtered.length} 部漫画，共 {filtered.reduce((s, c) => s + c.chapters.length, 0)} 章漫画网漫画目录
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-8 md:py-10">
        <div className="container-x">
          {/* Category switcher */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 flex items-center gap-1.5 text-sm font-semibold text-foreground-800">
              <i className="ri-price-tag-3-line text-primary-500"></i> 分类：
            </span>
            <Link
              to="/category/all"
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                activeId === "all" ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
              }`}
            >
              全部
            </Link>
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeId === c.id ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>

          {/* Filters */}
          <div className="mt-6 flex flex-col gap-4 border-y border-background-200/70 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 flex items-center gap-1.5 text-sm font-semibold text-foreground-800">
                <i className="ri-flag-2-line text-primary-500"></i> 状态：
              </span>
              {(["all", "连载中", "已完结"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setStatus(s);
                    setPage(1);
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    status === s ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {s === "all" ? "全部" : s}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 flex items-center gap-1.5 text-sm font-semibold text-foreground-800">
                <i className="ri-sort-desc text-primary-500"></i> 排序：
              </span>
              {(
                [
                  { k: "update", label: "最新更新" },
                  { k: "views", label: "人气最高" },
                  { k: "rating", label: "评分最高" },
                ] as const
              ).map((s) => (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => changeSort(s.k)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    sortKey === s.k ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          {pageItems.length > 0 ? (
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {pageItems.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center py-16 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-sm text-foreground-500">该分类下暂无符合条件的漫画</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-10 flex flex-wrap items-center justify-center gap-2" aria-label="分页导航">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={current === 1}
                className="flex h-10 items-center gap-1 rounded-md bg-background-100 px-4 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="ri-arrow-left-s-line"></i> 上一页
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  aria-current={n === current ? "page" : undefined}
                  className={`flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors ${
                    n === current ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={current === totalPages}
                className="flex h-10 items-center gap-1 rounded-md bg-background-100 px-4 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                下一页 <i className="ri-arrow-right-s-line"></i>
              </button>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}